import { action, computed, makeObservable, observable } from 'mobx';
import { Operator } from '../../components/Module/Table/Filters/FilterInput/constants';
import { WithOptional } from '../../utils/typeUtils';
import { FilterSearchGroups, IFilterSearchGroups, FilterParam } from './Filter';

export interface FilterSearchParamsControllerOptions {
  orParams: FilterParam[][];
  andParams?: FilterParam[];
}

export class FilterSearchParamsController implements IFilterSearchGroups {
  @observable private _value: FilterParam[][];

  private _filterSearchParams: FilterSearchGroups;
  private _initialValue: { or: FilterParam[][]; andParams: FilterParam[] };

  constructor({
    orParams: initialParams,
    andParams = [],
  }: FilterSearchParamsControllerOptions) {
    this._initialValue = { or: initialParams, andParams };
    this._filterSearchParams = new FilterSearchGroups(initialParams, andParams);
    this._value = [...this._filterSearchParams.entries()].map((v) => [...v]);

    makeObservable(this);
  }

  @computed get value() {
    return this._value;
  }

  get = (field: string, operator?: Operator) => {
    return this._filterSearchParams.get(field, operator);
  };

  getAll = (field: string, operator?: Operator) => {
    return this._filterSearchParams.getAll(field, operator);
  };

  is = (value: FilterParam[][]): boolean => {
    return this._filterSearchParams.is(value);
  };

  includes = (value: FilterParam[][]): boolean => {
    return this._filterSearchParams.includes(value);
  };

  addAndParams = (params: FilterParam[]) => {
    this._filterSearchParams.addAndParams(params);

    this._update();
  };

  setAndParams = (params: FilterParam[]) => {
    this._filterSearchParams.setAndParams(params);

    this._update();
  };

  deleteParams = (params: WithOptional<FilterParam, 'value'>[]) => {
    this._filterSearchParams.deleteParams(params);

    this._update();
  };

  deleteAndParams = (params: WithOptional<FilterParam, 'value'>[]) => {
    this._filterSearchParams.deleteAndParams(params);

    this._update();
  };

  addGroup = (params: FilterParam[]): void => {
    this._filterSearchParams.addGroup(params);

    this._update();
  };

  deleteGroup = (groupIndex: number): void => {
    this._filterSearchParams.deleteGroup(groupIndex);

    this._update();
  };

  setOrParams = (params: FilterParam[][]) => {
    this._filterSearchParams.setGroups(params);

    this._update();
  };

  deleteGroupParam = (
    groupIndex: number,
    field: string,
    value?: string,
    operator?: Operator,
  ): void => {
    this._filterSearchParams.deleteGroupParam(
      groupIndex,
      field,
      value,
      operator,
    );

    this._update();
  };

  setGroupParam = (
    groupIndex: number,
    field: string,
    value: string,
    operator?: Operator,
  ): void => {
    this._filterSearchParams.setGroupParam(groupIndex, field, value, operator);

    this._update();
  };

  appendGroupParam = (
    groupIndex: number,
    field: string,
    value: string,
    operator?: Operator,
  ): void => {
    this._filterSearchParams.appendGroupParam(
      groupIndex,
      field,
      value,
      operator,
    );

    this._update();
  };

  /** Перезаписывает значение фильтров */
  set = (or: FilterParam[][], and?: FilterParam[]) => {
    this._filterSearchParams = new FilterSearchGroups(or, and);

    this._update();
  };

  /** Присваивает начальное значение фильтра */
  reset = () => {
    this._filterSearchParams = new FilterSearchGroups(
      this._initialValue.or,
      this._initialValue.andParams,
    );

    this._update();
  };

  /** Очищает фильтр */
  clear = () => {
    this._filterSearchParams = new FilterSearchGroups([]);

    this._update();
  };

  @action private _update = () => {
    this._value = [...this._filterSearchParams.entries()].map((v) => [...v]);
  };
}
